import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationUnitDocument,
  UpdateOrganizationUnitDocument,
  DeleteOrganizationUnitDocument,
  type GetOrganizationUnitQuery,
  type UpdateOrganizationUnitMutation,
  type UpdateOrganizationUnitMutationVariables,
  type DeleteOrganizationUnitMutation,
  type DeleteOrganizationUnitMutationVariables,
} from '@/generated/root-types';
import { setOverrideTenantId } from '@gastrosoft/shared/utils';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import {
  detailBackButtonSx,
  detailFieldGridSx,
  detailFieldSx,
  detailFullRowSx,
  detailHeaderRowSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

export default function OrganizationUnitDetail() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [branchType, setBranchType] = useState('');
  const [appType, setAppType] = useState('');
  const [sort, setSort] = useState<string>('');
  const [additionalPropertiesJson, setAdditionalPropertiesJson] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetOrganizationUnitQuery>(
    GetOrganizationUnitDocument,
    {
      variables: { id: id! },
      skip: !id,
    },
  );

  const unit = data?.organizationUnit;

  useEffect(() => {
    if (unit?.tenantId) setOverrideTenantId(unit.tenantId);
    else setOverrideTenantId(null);
    return () => setOverrideTenantId(null);
  }, [unit?.tenantId]);

  useEffect(() => {
    if (!unit) return;
    setName(unit.name);
    setDescription(unit.description ?? '');
    setBranchType(unit.branchType ?? '');
    setAppType(unit.appType ?? '');
    setSort(String(unit.sort ?? ''));
    setAdditionalPropertiesJson(unit.additionalPropertiesJson ?? '');
  }, [unit]);

  const [updateUnit, { loading: saving }] = useMutation<
    UpdateOrganizationUnitMutation,
    UpdateOrganizationUnitMutationVariables
  >(UpdateOrganizationUnitDocument);

  const [deleteUnit, { loading: deleting }] = useMutation<
    DeleteOrganizationUnitMutation,
    DeleteOrganizationUnitMutationVariables
  >(DeleteOrganizationUnitDocument);

  const handleSave = async () => {
    if (!id) return;
    try {
      const res = await updateUnit({
        variables: {
          input: {
            id,
            name,
            description: description || undefined,
            branchType: branchType || undefined,
            appType: appType || undefined,
            sort: sort === '' ? undefined : parseInt(sort, 10),
            additionalPropertiesJson: additionalPropertiesJson.trim() || undefined,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.updateOrganizationUnit);
      if (pe) {
        showError(pe);
        return;
      }
      showSuccess(t('common.saved'));
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const res = await deleteUnit({ variables: { input: { id } } });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.deleteOrganizationUnit);
      if (pe || !res.data?.deleteOrganizationUnit?.boolean) {
        showError(pe || t('errors.generic'));
        return;
      }
      showSuccess(t('common.deleted'));
      navigate('/organization-units');
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...detailPageContainerSx, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !unit) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/organization-units')}>
          {t('common.back')}
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>
          {error?.message ?? t('errors.generic')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={detailHeaderRowSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/organization-units')}
          sx={detailBackButtonSx}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {unit.name}
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Box sx={detailFieldGridSx}>
          <TextField
            sx={detailFieldSx}
            label={t('common.id')}
            value={unit.id}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgUnits.databaseId')}
            value={unit.databaseId ?? '–'}
            fullWidth
            InputProps={{ readOnly: true }}
            helperText={t('masterData.apiFieldReadOnly')}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.organizationId')}
            value={unit.organizationId}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.tenantId')}
            value={unit.tenantId}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('organizationDetail.customer')}
            value={unit.organization?.name ?? '–'}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.tenant')}
            value={unit.tenant?.name ?? '–'}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.created')}
            value={formatDateTime(unit.createdAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.updated')}
            value={formatDateTime(unit.updatedAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('common.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgUnits.branchType')}
            value={branchType}
            onChange={(e) => setBranchType(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgUnits.appType')}
            value={appType}
            onChange={(e) => setAppType(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgUnits.sort')}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            fullWidth
            type="number"
          />
          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('orgUnits.additionalPropertiesJson')}
            value={additionalPropertiesJson}
            onChange={(e) => setAdditionalPropertiesJson(e.target.value)}
            fullWidth
            multiline
            minRows={4}
          />
          <Box sx={{ ...detailFullRowSx, display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {t('common.save')}
            </Button>
            {superAdmin && (
              <Button color="error" onClick={() => setDeleteOpen(true)}>
                {t('common.delete')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('orgUnits.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('orgUnits.confirmDeleteBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
